import {
  BookOpen,
  Braces,
  CalendarClock,
  Database,
  HardDriveDownload,
  KeyRound,
  LayoutDashboard,
  LifeBuoy,
  Network,
  Server,
  Settings,
  Users,
} from "lucide-react";
import { matchPath, useLocation } from "react-router-dom";

import { AppShell, type NavItem } from "@/client/components/app-shell";
import { DeactivationBanner } from "@/client/components/deactivation-banner";
import { ImpersonationBanner } from "@/client/components/impersonation-banner";
import { MaintenanceBanner } from "@/client/components/maintenance-banner";
import { PlacementsProvider } from "@/client/features/docs/placements";
import { useAuthStore } from "@/client/features/auth/auth-store";

export function CustomerShell() {
  // Impersonation carries the tenant name in its context; a real customer's
  // name rides on the session principal. Either way we personalize the shell.
  const orgName = useAuthStore((s) => s.impersonation?.tenantName ?? s.user?.tenantName ?? null);
  const ticketsEnabled = useAuthStore((s) => s.features.tickets);
  const nodesEnabled = useAuthStore((s) => s.features.nodes);
  const pxeEnabled = useAuthStore((s) => s.features.pxe);
  const sshKeysEnabled = useAuthStore((s) => s.features.sshKeys);
  const networkAccessEnabled = useAuthStore((s) => s.features.networkAccess);
  const storageEnabled = useAuthStore((s) => s.features.storage);
  // API tokens unlock non-interactive Redfish access, so they ride the same
  // bmc entitlement as the BMC/console surface they authenticate against. The
  // page is tenant-admin only (create/list/revoke all require it server-side),
  // so only show the nav to admins - a member would just hit a 403 load error.
  // The impersonation principal is a synthetic tenant_admin, so staff view-as
  // still sees it (read-only).
  const bmcEnabled = useAuthStore((s) => s.features.bmc);
  const isTenantAdmin = useAuthStore((s) => s.user?.tenantRole === "tenant_admin");
  const apiTokensEnabled = bmcEnabled && isTenantAdmin;

  // The per-node Console & power tab is a focused console tool - suppress the
  // banners there so the console gets the full height (PE-950).
  const location = useLocation();
  const onConsoleTab = matchPath("/nodes/:id/console", location.pathname) !== null;

  const nav: NavItem[] = [
    { label: "Dashboard", to: "/", icon: LayoutDashboard, end: true },
    ...(nodesEnabled ? [{ label: "Nodes", to: "/nodes", icon: Server } satisfies NavItem] : []),
    ...(storageEnabled
      ? [{ label: "Storage", to: "/storage", icon: Database } satisfies NavItem]
      : []),
    { label: "Maintenance", to: "/calendar", icon: CalendarClock },
    ...(sshKeysEnabled
      ? [{ label: "SSH keys", to: "/ssh-keys", icon: KeyRound } satisfies NavItem]
      : []),
    ...(apiTokensEnabled
      ? [{ label: "API tokens", to: "/api-tokens", icon: Braces } satisfies NavItem]
      : []),
    ...(networkAccessEnabled
      ? [{ label: "Network access", to: "/allowed-ips", icon: Network } satisfies NavItem]
      : []),
    ...(pxeEnabled
      ? [{ label: "Boot menu", to: "/pxe", icon: HardDriveDownload } satisfies NavItem]
      : []),
    ...(ticketsEnabled
      ? [{ label: "Support", to: "/tickets", icon: LifeBuoy } satisfies NavItem]
      : []),
    { label: "Team", to: "/team", icon: Users },
    { label: "Docs", to: "/docs", icon: BookOpen },
    { label: "Settings", to: "/settings", icon: Settings },
  ];

  return (
    <PlacementsProvider>
      <AppShell
        audienceLabel="Portal"
        orgName={orgName}
        nav={nav}
        homeTo="/"
        topSlot={
          onConsoleTab ? null : (
            <>
              <ImpersonationBanner />
              <DeactivationBanner />
              <MaintenanceBanner />
            </>
          )
        }
      />
    </PlacementsProvider>
  );
}
